import moment from 'moment'

/**
 * @typedef {import('../../js-doc').V2CreateReserveRequest} V2CreateReserveRequest
 * @typedef {import('../../js-doc').V2ReserveResponse} V2ReserveResponse
 * @typedef {import('../../js-doc').ReservePayload} ReservePayload
 * @typedef {import('../../js-doc').V2DefaultReserveMetadata} V2DefaultReserveMetadata
 * @typedef {import('../../js-doc').V2DirectPaymentByClientReserveMetadata} V2DirectPaymentByClientReserveMetadata
 * @typedef {import('../../js-doc').V2NonFactoredPaymentReserveMetadata} V2NonFactoredPaymentReserveMetadata
 * @typedef {import('../../js-doc').V2ReleaseOfFundsTo3rdPartyReserveMetadata} V2ReleaseOfFundsTo3rdPartyReserveMetadata
 * @typedef {import('../../js-doc').V2TransferFromReserveMetadata} V2TransferFromReserveMetadata
 * @typedef {import('../../js-doc').V2TransferToReserveMetadata} V2TransferToReserveMetadata
 * @typedef {import('../../js-doc').V1ReserveResponse} V1ReserveResponse
 */

/**
 * Maps the V1 request body and V1 response body to a V2 request body
 *
 * @param {*} v1RequestBody - Represents the V1 create request body
 * @param {*=} v1ResponseBody - Represents the V1 create response body
 * @param {*} ingestingFromV2 - Whether we're ingesting into V2 then to V1
 * @returns {V2CreateReserveRequest}
 */
export const mapV2CreateReserveRequest = (v1RequestBody, v1ResponseBody, ingestingFromV2 = false) => {
  return {
    __ingestThrough: ingestingFromV2,
    __v1Payload: v1RequestBody,
    amount: v1RequestBody.amount,
    id: v1ResponseBody?.id,
    note: v1RequestBody.notes,
    payload: mapV1RequestBodyToV2RequestBodyMetadata(v1RequestBody),
  }
}

/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {ReservePayload | {}}
 */
const mapV1RequestBodyToV2RequestBodyMetadata = (v1RequestBody) => {
  switch (v1RequestBody.reason) {
    case 'balance transfer from (positive)':
      return mapV1RequestBodyToV2TransferFromPositiveMetadata(v1RequestBody)
    case 'balance transfer to (positive)':
      return mapV1RequestBodyToV2TransferToPositiveMetadata(v1RequestBody)
    case 'direct payment by client':
      return mapV1RequestBodyToV2DirectPaymentByClientMetadata(v1RequestBody)
    case 'non-factored payment':
      return mapV1RequestBodyToV2NonFactoredPaymentMetadata(v1RequestBody)
    case 'release to 3rd party':
      return mapV1RequestBodyToV2ReleaseOfFundsTo3rdPartyMetadata(v1RequestBody)
    case 'balance transfer from':
      return mapV1RequestBodyToV2TransferFromPositiveMetadata(v1RequestBody)
    case 'balance transfer to':
      return mapV1RequestBodyToV2TransferToPositiveMetadata(v1RequestBody)
    default:
      return mapV1RequestBodyToV2DefaultMetadata(v1RequestBody)
  }
}

const mapV1RequestBodyReasonToV2RequestBodyMetadataType = (v1RequestBody) => {
  const v1TypesToV2Types = {
    'balance transfer from (positive)': 'transferFromPositive',
    'balance transfer to (positive)': 'transferToPositive',
    'balance transfer from': 'transferFrom',
    'balance transfer to': 'transferTo',
    'client credit': 'clientCredit',
    'debtor claim': 'brokerClaim',
    'direct payment by client': 'directPaymentByClient',
    'fee': 'fee',
    'non-factored payment': 'nonFactoredPayment',
    'overadvance': 'overadvance',
    'release of funds': 'releaseOfFunds',
    'release to 3rd party': 'releaseOfFundsTo3rdParty',
    'write off': 'writeOff',
  }

  return v1TypesToV2Types[v1RequestBody.reason]
}

/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2DefaultReserveMetadata}
 */
const mapV1RequestBodyToV2DefaultMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    receivedDate: v1RequestBody.received_date,
  }
}

/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2DirectPaymentByClientReserveMetadata}
 */
const mapV1RequestBodyToV2DirectPaymentByClientMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    receivedDate: moment(parseInt(v1RequestBody.metadata.date_received)).toISOString(),
  }
}

/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2NonFactoredPaymentReserveMetadata}
 */
const mapV1RequestBodyToV2NonFactoredPaymentMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    batchDate: moment(parseInt(v1RequestBody.metadata.date_received)).toISOString(),
    brokerId: v1RequestBody.metadata.debtor_id,
    brokerName: v1RequestBody.metadata.debtor_name,
    checkNumber: v1RequestBody.metadata.check_number,
    loadNumber: v1RequestBody.metadata.load_number,
    type: v1RequestBody.metadata.transfer_type === "check"
      ? "Check"
      : "ACH",
  }
}

/* eslint-disable max-len */
/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2ReleaseOfFundsTo3rdPartyReserveMetadata}
 */
/* eslint-enable max-len */
const mapV1RequestBodyToV2ReleaseOfFundsTo3rdPartyMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    thirdParty: v1RequestBody.metadata.third_party,
  }
}

/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2TransferFromReserveMetadata}
 */
const mapV1RequestBodyToV2TransferFromPositiveMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    transferClientId: v1RequestBody.transfer_clientid,
  }
}

/**
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2TransferToReserveMetadata}
 */
const mapV1RequestBodyToV2TransferToPositiveMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    transferClientId: v1RequestBody.transfer_clientid,
  }
}
